/** @format */

import {
	Component,
	ComponentRef,
	effect,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
	input,
	viewChild,
	computed
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
	AnalyticsService,
	BreakpointsService,
	DynamicTemplateLoader,
	UtilityService
} from '@shared/utils/services';
import { HomrDataService, HomrPage } from '@shared/homr/data-access';

import { KrakenComponent } from '@shared/components';
import { chain } from '@core/chain';
import { take } from 'rxjs/operators';
import { HmfDrawerComponent, HmfModalFactoryService } from '@dsg-tech/homefield-angular';
import { DynamicDrawerSizes, ProductSelectionUtilityService } from '@pdp/utils';
import { BreakPointNew } from '@shared/data-access/enums';

@Component({
    selector: 'pdp-component-size-chart-v2',
    templateUrl: './size-chart.component.html',
    styleUrls: ['./size-chart.component.scss'],
    standalone: false
})
export class SizeChartComponent {
	sizeChartName = input<string>();

	@ViewChild('LazySizeChartKraken', { read: ViewContainerRef, static: false })
	sizeChartKraken: ViewContainerRef;

	private sizeChartRef: ComponentRef<HmfDrawerComponent>;
	sizeChartContent = viewChild.required<TemplateRef<unknown>>('sizeChartContent');

	heading = 'Size Chart';

	sizeChartSurveyId = chain.sizeChartSurveyId;
	displayHtml: SafeHtml;
	isAem = false;

	loading = true;

	drawerDirection = computed(() =>
		this.breakpointsService.breakpoint() <= BreakPointNew.SMALL
			? 'bottom'
			: 'right'
	);

	constructor(
		private dltService: DynamicTemplateLoader,
		private homrDataService: HomrDataService,
		private utilityService: UtilityService,
		private pdslUtilityService: ProductSelectionUtilityService,
		private analyticsService: AnalyticsService,
		private sanitizer: DomSanitizer,
		private modalFactoryService: HmfModalFactoryService,
		private breakpointsService: BreakpointsService
	) {
		// When we update to Angular v20, we should update the modal service to make use of input binding instead
		// so we can just do that when we create the drawer instead of having to manually setInput in an effect
		// https://github.com/angular/angular/pull/60137
		effect(() => {
			const breakpoint = this.breakpointsService.breakpoint();
			if (this.sizeChartRef) {
				this.sizeChartRef.setInput(
					'size',
					this.pdslUtilityService.getValueByScreenSize(DynamicDrawerSizes, breakpoint)
				);
				this.sizeChartRef.setInput(
					'direction',
					this.drawerDirection()
				);
			}
		});
	}

	getSizeChart() {
		if (this.sizeChartKraken) {
			this.sizeChartKraken.clear();
		}
		const emsName = this.utilityService.convertSizeChartNameToNewFormat(this.sizeChartName());
		if (emsName.includes('aem')) {
			this.isAem = true;
			const url = `${this.getFirstLetter(this.sizeChartName())}/${this.sizeChartName()}/master.html`;
			//To get this call working locally, use CORS browser extension
			this.homrDataService.getAemData$(url).subscribe({
				next: (res) => {
					this.loading = false;
					this.displayHtml = this.sanitizer.bypassSecurityTrustHtml(res.toString());
				},
				error: () => {
					this.loading = false;
				}
			});
		} else {
			this.isAem = false;
			this.homrDataService
				.getData$('size-chart', 3, emsName)
				.pipe(take(1))
				.subscribe({
					next: (homrTemplate: HomrPage) => {
						this.lazySizeChartKraken(homrTemplate.content['Kraken Alpha']);
					},
					error: () => {
						this.loading = false;
					}
				});
		}
		this.openSizeChart();
		this.sendAnalyticsFor('Size Chart');
	}

	getFirstLetter(brand: string): string {
		const words = brand.split('_');
		const firstWord = words[0].toLowerCase();
		const secondWord = words[1];
		if (firstWord.includes('aem')) {
			return secondWord.charAt(0).toLowerCase();
		}
		return firstWord.charAt(0).toUpperCase();
	}

	/**
	 * @description lazy load Kraken component for size chart data
	 */
	async lazySizeChartKraken(data: { [key: string]: { [key: string]: string } }) {
		await import('@shared/components').then(({ KrakenModule }) => {
			this.dltService
				.createModuleAndReturnComponentRef(
					this.sizeChartKraken,
					KrakenModule,
					KrakenComponent,
					{
						data,
						addlData: { tagSectionNum: 1, widgetTypeTag: 'kraken' }
					}
				)
				.then((krakenComponent: KrakenComponent) => {
					krakenComponent.loadEvent.subscribe(() => {
						this.loading = false;
					});
				});
		});
	}

	openSizeChart() {
		if (!this.sizeChartRef) {
			this.sizeChartRef = this.modalFactoryService.create(
				HmfDrawerComponent,
				{
					headerText: this.heading,
					closable: true,
					closableVariant: 'primary',
					closableSide: 'right',
					direction: this.drawerDirection(),
					isCloseButtonSticky: true,
					size: this.pdslUtilityService.getValueByScreenSize(
						DynamicDrawerSizes,
						this.breakpointsService.breakpoint()
					)
				},
				this.sizeChartContent()
			);
		}

		this.sizeChartRef.instance.openDrawer();
	}

	sendAnalyticsFor(name: string) {
		/*
		 * Analytics Event
		 */
		// Set the modal name
		this.analyticsService.setModalName(name);

		// Set the Analytics event type
		this.analyticsService.setType('event', 'ModalOpen');

		// Trigger the Analytics update
		this.analyticsService.triggerAnalyticsEvent('reporting:update');
	}
}
